import { Component, Entity, registerComponent, registerSystem, System } from "aframe";
import { AnimationClip, Euler, Interpolant, Matrix4, Object3D, Quaternion, QuaternionLike, Vector3, Vector3Like } from "three";
import { projectFrom, subFromArray, transformPoint, transformVector, untransformVector } from "../three/math";
import { SetAnimationEventDetail } from "./avatar";
import { ClipComponent, LoadedClip } from "./animation/clip";


interface Transform
{
	position : Vector3Like;
	orientation : QuaternionLike;
}
type ApplyPose = ( transform : Transform, worldMatrix : Matrix4 ) => void;
type BoneMap = { [name:string] : ApplyPose };

interface VrBodyComponent extends Component
{
	data : 
	{
		crouch : Entity;
		bodyFilter : string;
		leftHand : Entity;
		rightHand : Entity;
	};
	system : VrBodySystem;
	bones : BoneMap;
	crouchClip : AnimationClip | null;
	updateBones() : void;
	updateFrame(frame : XRFrame, xr : XRSession) : void;

	leftHandRotation : { x:number, z:number, order:string };
	rightHandRotation : { x:number, z:number, order:string };
}
interface VrBodySystem extends System
{
	instance : VrBodyComponent | null;
	bonesMapping : { [ joint : string ] : string | null };
}




const traverseBones = (o : Object3D, vrBody : VrBodyComponent, map : BoneMap) =>
{
	let name = o.name, mapName = o.name;
	let slot : Object3D | null = null;

	for(let child of o.children)
	{
		const childEl = (child as Object3D & { el? : Entity }).el;
		if(childEl && (childEl !== vrBody.el))
		{
			if( (name == "LeftHand") && childEl.components["xr-input__left-gamepad"] )
				slot = child;
			else if( (name == "RightHand") && childEl.components["xr-input__right-gamepad"] )
				slot = child;
			if(! (child as Object3D & { isBone ? : boolean }).isBone ) //Continue traversing bones that were taken by other entities.
				continue;
		}
		traverseBones(child, vrBody, map);
	}

	if(name.startsWith("Left")) name = name.slice(4);
	else if(name.startsWith("Right")) name = name.slice(5);

	let ik : ApplyPose | null = null;

	if(name == "Head") ik = createBodyIk(vrBody.el.object3D, o, vrBody.crouchClip);
	else if(name == "Hand") ik = createArmIk(o, slot);
	else if(name.startsWith("Hand")) ik = createFingerIk(o);
	
	if(ik)
		map[mapName] = ik;
};



const applyArmIk = (() =>
{
	const SP = new Vector3(), SH = new Vector3(), Q1 = new Quaternion();

	return (hand : Object3D, elbow : Object3D, shoulder : Object3D, position : Vector3, quaternion : Quaternion, side : number ) =>
	{
		subFromArray( SP.copy( position ), shoulder.matrixWorld.elements, 12);
		const sp2 = SP.lengthSq(), se2 = elbow.position.lengthSq(), eh2 = hand.position.lengthSq();
		const se = Math.sqrt(se2), eh = Math.sqrt(eh2);
	
		elbow.rotation.set(0,0,0,'ZXY');
		const cosRZ = 0.5 * (sp2 - (se2 + eh2)) / (se*eh);
		if(cosRZ > 1.0) //Too far away
		{
			elbow.rotation.z = 0.0;
			if(hand.parent === elbow)
			{
				//The less intrusive solution is to insert a parent between hand and elbow :
				// - it does not induce scaling, especially non-uniform
				// - it preserves the hand's local position
				const goGoGadgetArm = new Object3D();
				goGoGadgetArm.add(hand);
				elbow.add(goGoGadgetArm);
			}
			hand.parent!.position.set(0, Math.sqrt(sp2) - (se+eh), 0);
		}
		else
		{
			if(hand.parent !== elbow)
				hand.parent!.position.set(0,0,0);
			if(cosRZ < -1.0) //Too close
				elbow.rotation.z = Math.PI;
			else
				elbow.rotation.z = side * Math.acos(cosRZ);
			
		} 
	
	
		SH.set( -eh * Math.sin(elbow.rotation.z), se + eh * Math.cos(elbow.rotation.z), 0.0).normalize();
		untransformVector(SP, shoulder.parent!.matrixWorld).normalize();
		shoulder.quaternion.setFromUnitVectors(SH, SP);
	
		projectFrom(SH, SP);

		const theta = -0.3 * side * Math.acos( -SH.z );
		shoulder.quaternion.premultiply( Q1.setFromAxisAngle(SP, theta) );
	
		shoulder.updateWorldMatrix(false, false);
		elbow.updateWorldMatrix(false, false);
	
		//Q1 = inverse elbow orientation
		//Q1.setFromRotationMatrix(elbow.matrixWorld).conjugate(); 
		elbow.matrixWorld.decompose({} as Vector3, Q1, {} as Vector3);
		
		hand.rotation.order = 'YXZ';
		hand.quaternion.multiplyQuaternions(Q1.conjugate(), quaternion);
	
		//Distribute rotation between hand and forearm
		hand.rotation.y *= 0.5;
		elbow.rotation.y = hand.rotation.y;

		elbow.updateWorldMatrix(false, true);
	};
})(); 


// WebXR to ReadyPlayer.me joints orienation offset
const yzRot = new Quaternion(0, Math.SQRT1_2, -Math.SQRT1_2, 0);
const yRot = new Quaternion(0, 1, 0, 0);

const createArmIk = (hand : Object3D, slot : Object3D | null) =>
{
	const elbowName = hand.name.replace("Hand", "ForeArm");
	const shoulderName = hand.name.replace("Hand", "Arm");
	let elbow : Object3D | null = null, shoulder : Object3D | null = null;
	for(let parent = hand.parent; parent; parent = parent.parent)
	{
		if(parent.name == elbowName)
			elbow = parent;
		else if(parent.name == shoulderName)
			shoulder = parent;
		if( elbow && shoulder )
			break;
	}
	if(!(elbow && shoulder)) return null;

	const side = hand.name.startsWith("Left") ? 1 : -1;

	const pos = new Vector3(), vtemp = new Vector3(); 
	const rot = new Quaternion(), qtemp = new Quaternion();
	if(slot)
	{
		return  (transform : Transform, worldMatrix : Matrix4) => 
		{
			//World matrix :
			transformPoint(pos.copy(transform.position), worldMatrix);
			worldMatrix.decompose( {} as Vector3, qtemp, {} as Vector3 );
			rot.copy(transform.orientation).premultiply(qtemp);
			
			//Slot :
			rot.multiply(yzRot).multiply(qtemp.copy(slot.quaternion).conjugate());
			pos.sub(vtemp.copy(slot.position).applyQuaternion(rot));

			applyArmIk(hand, elbow!, shoulder!, pos, rot, side);
		};
	}

	
	return (transform : Transform, worldMatrix : Matrix4) => 
	{
		//World matrix :
		transformPoint(pos.copy(transform.position), worldMatrix);
		worldMatrix.decompose( {} as Vector3, qtemp, {} as Vector3 );
		rot.copy(transform.orientation).premultiply(qtemp);

		//XR to RPM
		rot.multiply(yzRot);
		
		applyArmIk(hand, elbow!, shoulder!, pos, rot, side );
	};
}


const applyFingerIk = (() =>
{
	const tpos = new Vector3(), cpos = new Vector3();

	return  ( parent : Object3D, child : Object3D, childPosition : Vector3Like, worldMatrix : Matrix4 ) =>
	{
		//Get target position in parent's parent transform
		subFromArray( tpos.copy( childPosition ).applyMatrix4(worldMatrix), parent.matrixWorld.elements, 12 );
		parent.parent && untransformVector( tpos, parent.parent!.matrixWorld );

		parent.quaternion.setFromUnitVectors( cpos.copy(child.position).normalize(), tpos.normalize() );
		parent.updateWorldMatrix(false, true);
	};

})(); 


const createFingerIk = (child : Object3D) =>
{
	const baseName = child.name.slice(0, -1);
	for(let parent = child.parent; parent; parent = parent.parent)
		if(parent.name.startsWith(baseName))
			return (transform : Transform, worldMatrix : Matrix4) => 
				applyFingerIk(parent!, child, transform.position, worldMatrix);

	return null;
};




type ClipInterpolation = (time : number) => void;
const createBodyIk = (root : Object3D, headBone : Object3D, crouchClip : AnimationClip | null) =>
{
	const mainEye = headBone.getObjectByName("LeftEye") || headBone;
	let spine : Object3D | null = null, neck : Object3D | null = null, hips : Object3D | null = null, spineLength = headBone.position.length();
	for(let p = headBone.parent; p; p = p.parent)
	{
		if(p === root)
			break;
		if(p.name == "Hips")
		{
			hips = p;
			break;
		}
		if(p.name == "Neck")
		{
			neck = p;
			spineLength += p.position.length();
		}
		if(p.name.startsWith("Spine"))
			spineLength += p.position.length();
		if(p.name == "Spine")
			spine = p;
	}

	if(!(hips && spine))
		return null;
	if(!crouchClip)
		return null;	//Todo


	//Add a free parent to spine
	let spineRoot = spine.parent;
	if(spineRoot === hips)
	{
		spineRoot = new Object3D();
		spineRoot.name = "SpineRoot";
		spineRoot.add(spine);
		hips.add(spineRoot);
	}


	const crouchInterpolants : { [name:string] : { interpolant : Interpolant, targets : Object3D[] } } = {};
	for(let trk of crouchClip.tracks)
		crouchInterpolants[trk.name] = { interpolant : trk.createInterpolant(), targets : [] };
	
	const traverseLegs = (obj : Object3D) =>
	{
		const ipol = crouchInterpolants[ obj.name + ".quaternion" ];
		if(ipol)
			ipol.targets.push(obj);
		for(let child of obj.children)
			traverseLegs(child);
	};
	for(let child of hips.children)
		if(child !== spineRoot)
		traverseLegs(child);
	

	const hipsIpol = crouchInterpolants["Hips.position"].interpolant;
	const sampleCount = 64, hipsSamples : number[] = [];
	for(let n=0; n <= sampleCount; ++n)
	{
		const t = crouchClip.duration * n / sampleCount;
		hipsSamples[n] = ( hipsIpol?.evaluate(t) as Float32Array )[1];
	}

	const getCrouchTime = (desiredHipsHeight : number) =>
	{
		let s = 0, e = sampleCount, i, h;
		while(s < e)
		{
			h = hipsSamples[ i = Math.floor( (s+e)/2 ) ];
			if(h == desiredHipsHeight) break;
			if(h < desiredHipsHeight) s = i+1;
			else e = i;
		}
		h = hipsSamples[s];
		s += (desiredHipsHeight - h) / ( hipsSamples[s+1] - h );
		return crouchClip.duration * (s / sampleCount);
	}

	const Q = new Quaternion(), Q2 = new Quaternion();
	const V = new Vector3(), V2 = new Vector3();
	const M = new Matrix4();
	return (transform : Transform, world : Matrix4) =>
	{
		//Start working in camera-local space
		M.makeRotationFromQuaternion(Q.copy(transform.orientation));
		V.copy(transform.position);
		if( mainEye !== headBone )
			V.sub( transformVector(V2.copy(mainEye.position), M) );
		
		let height = V.y;
		
		const hipsT = getCrouchTime(height - spineLength);
		hips!.position.fromArray( hipsIpol!.evaluate(hipsT) as Float32Array );
		hips!.updateMatrix();
		spineRoot!.quaternion.copy( hips!.quaternion ).conjugate();

		for(let key in crouchInterpolants)
		{
			const ipol = crouchInterpolants[key];
			for(let target of ipol.targets)
				target.quaternion.fromArray( ipol.interpolant.evaluate(hipsT) as Float32Array );
		}

		height -= hips!.position.y;

		const globalHeadAngle = -Math.asin( M.elements[9] );
		let rx = 0, neckAngle = 0.4 + 0.5 * globalHeadAngle;
		for(let min=0, max=1, n=16; --n >= 0; )
		{
			rx = 0.5 * (min + max);
			const cos = Math.cos(rx), sin = Math.sin(rx);
			let dx = 0, dy = 0, spineAngle = 0;
			for(let o = neck!; o !== spineRoot; o = o.parent!)
			{
				spineAngle += rx;
				dy += o.position.y;
				[ dy, dx ] = [ dy * cos - dx * sin, dx * cos + dy * sin ];
			}

			neckAngle = 0.4 + 0.5 * (globalHeadAngle - spineAngle);
			dy += neck!.position.y * Math.cos(spineAngle + neckAngle);
			if(dy < height) max = rx;
			else if(dy > height) min = rx;
			else break;
		}
		neck!.rotation.set(neckAngle, 0, 0);
		Q2.set( Math.sin(0.5*rx), 0, 0, Math.cos(0.5*rx) );
		for(let o = neck!.parent!; o !== spineRoot; o = o.parent!)
			o.quaternion.copy(Q2);
		spineRoot.quaternion.multiply(Q2);

		//Convert targets to world :
		transformPoint( V, world );
		world.decompose( {} as Vector3, Q2, {} as Vector3 );	
		Q.premultiply( Q2 );

		/* Already handled by a Tranform component
		//Align head with target orientation
		headBone.parent!.updateWorldMatrix(true, false);
		//Q2.setFromRotationMatrix(headBone.parent!.matrixWorld).conjugate();
		headBone.parent!.matrixWorld.decompose( {} as Vector3, Q2, {} as Vector3 );	
		headBone.quaternion.multiplyQuaternions(Q2.conjugate(), Q);
		*/

		//Move Hips to match mainEye
		headBone.updateWorldMatrix(true, false);
		subFromArray(V2.copy(V), headBone.matrixWorld.elements, 12);
		hips!.parent && untransformVector(V2, hips!.parent.matrixWorld);
		hips!.position.add(V2);
		hips!.updateWorldMatrix(false, true);
	};
	
};

/*
interface VrBodyTestComponent extends Component
{
	position : Vector3;
	orienation : Quaternion;
	world : Matrix4;
	ik : ApplyPose | null;
	data : {
		target : Entity,
		crouch : ClipEntity
	}
}

registerComponent("vr-body-test", {
	schema : {
		target : { type : "selector" },
		crouch : { type : "selector" }
	},
	init(this : VrBodyTestComponent)
	{
		this.world = (new Matrix4()).identity();
		this.position = new Vector3();
		this.orienation = new Quaternion();
	},
	update(this : VrBodyTestComponent)
	{

		if( this.data.crouch )
		{
			const clip = this.data.crouch.components.clip;
			clip && clip.loaded.then( async (animClip : LoadedClip) =>
			{
				const root = await waitForObject3D(this.el);
				const head = root && root.getObjectByName("Head");
				if(head)
					this.ik = createBodyIk(root, head, animClip.clip);
			});
		}
		//createBodyIk(this.el.object3D, this.el.object3D.getObjectByName("Head"), this.data.crouch)
	},
	tick(this : VrBodyTestComponent)
	{
		if(!this.data.target) return;
		const M = this.data.target.object3D.matrixWorld;
		this.el.object3D.position.set(M.elements[12], 0, M.elements[14]);
		if(this.ik && this.data.target)
		{
			this.position.setFromMatrixPosition(M);
			this.orienation.setFromRotationMatrix(M);
			this.ik( { position : this.position, orientation : this.orienation} , this.world );
		}
	}
});
*/

const cameraToHead : Quaternion = new Quaternion(0, 1, 0 ,0);

// Grip space is the controller's handle, but it is held with a ~30° angle
// TODO : make it a parameter
const gripSpaceRotationOffset : Quaternion = new Quaternion( Math.sin(Math.PI / 12), 0, 0, Math.cos(Math.PI / 12) );

registerComponent("vr-body", {

	schema : {
		crouch : { type : "selector" },
		bodyFilter : { type : "string", default : "^(?:(?!Head\/|Hand\/).)*$" }
	},
	
	init(this : VrBodyComponent)
	{
		this.leftHandRotation = { x : 0.5, z : 0.0, order : "YXZ" };
		this.rightHandRotation = { x : 0.5, z : 0.0, order : "YXZ" };

		this.system.instance = this;
		this.crouchClip = null;
		this.updateBones();
		this.el.addEventListener("object3dset", (e) => this.updateBones());

		const scene = this.system.el;
		scene.addEventListener("enter-vr", () =>
		{
			const detail : SetAnimationEventDetail = 
			{
				level : 1,
				animations : {
					".*" : { 
						filter : this.data.bodyFilter,
						clips : []
					}
				}
			};
			this.el.emit("set-animation", detail );
		});
		scene.addEventListener("exit-vr", () =>
		{
			const detail : SetAnimationEventDetail = 
			{
				level : 1,
				animations : {}
			};
			this.el.emit("set-animation", detail );
		});
	},

	update(this : VrBodyComponent)
	{
		if( this.data.crouch )
		{
			const clip = this.data.crouch.components["clip"] as ClipComponent;
			clip && clip.loaded.then( (clipObject : Object3D) =>
			{
				this.crouchClip = clipObject.animations[0];
				this.updateBones();
			});
		}
	},
	
	
	remove(this : VrBodyComponent)
	{
		this.system.instance = null;
	},
	
	updateBones(this : VrBodyComponent)
	{
		const map : BoneMap = {};
		for(let key in this.el.object3DMap)
			traverseBones(this.el.object3DMap[key], this, map );
		this.bones = map;
	},
	
	updateFrame(this : VrBodyComponent, frame : XRFrame, xr : XRSession )
	{
		const camera = this.system.el.camera.el.object3D;
		const globalOffset = camera.parent!.matrixWorld;

		const applyBody = this.bones["Head"];
		applyBody && applyBody( { 
			position : camera.position, 
			orientation : (new Quaternion()).copy(camera.quaternion).multiply(cameraToHead)
		}, globalOffset );

		const refSpace = this.system.el.renderer.xr.getReferenceSpace()!;
		
		for(let srcIndex = xr.inputSources.length; --srcIndex >= 0; )
		{
			const c = xr.inputSources[srcIndex];

			const sideName = c.handedness.slice(0, 1).toUpperCase() + c.handedness.slice(1);

			if( c.hand )
			{
				if( !frame.getJointPose )
					continue;//Argh

				for(let joint of Array.from(c.hand.values()))
				{
					const name = this.system.bonesMapping[ joint.jointName ];
					if(!name)
						continue;

					const apply = this.bones[ sideName + name ];
					if(!apply)
						continue;

					const jointPose = frame.getJointPose(joint, refSpace);
					jointPose && apply(jointPose.transform, globalOffset);
				}
			}
			else if( c.gamepad )
			{
				const apply = this.bones[ sideName + "Hand" ];
				if(!apply)
					continue;

				const jointPose = c.gripSpace && frame.getPose( c.gripSpace, refSpace);
				if(jointPose)
				{
					const q = new Quaternion();
					q.copy(jointPose.transform.orientation).multiply(gripSpaceRotationOffset);
					
					const t:  Transform = { position : jointPose.transform.position, orientation : q };
					jointPose && apply(t, globalOffset);
				}
				else
				{
					const mat = camera.matrix.elements;
					const kDown = 0.1 * mat[9]; //0.1 when camera is looking down ; -0.1 when looking up (camera.z is backwards).
					const sideOffset = (sideName == "Left") ? (-0.1 + kDown) : (0.16 - kDown);
					const faceOffset = -0.1;
					const rot : Euler = (sideName == "Left") ?
						new Euler( (0.8 + kDown) * Math.PI, camera.rotation.y, -0.6 * Math.PI, 'YZX' ):
						new Euler( (0.8 + kDown) * Math.PI, camera.rotation.y, 0.6 * Math.PI, 'YZX' );
					

					const t:  Transform = {
						position : {
							x : camera.position.x + sideOffset * mat[0] + faceOffset * mat[8],
							y : camera.position.y - 0.4,
							z : camera.position.z + sideOffset * mat[2] + faceOffset * mat[10]
						},
						orientation : ((new Quaternion()).setFromEuler(rot))
					}
					apply(t, globalOffset);
				}

			}
		}
	}
});

registerSystem("vr-body", {
	
	init(this : VrBodySystem)
	{
		this.instance = null;
		
		//TODO : configurable
		this.bonesMapping = {
			'wrist': 'Hand',
			'thumb-metacarpal': 'HandThumb1',
			'thumb-phalanx-proximal': 'HandThumb2',
			'thumb-phalanx-distal': 'HandThumb3',
			'thumb-tip': 'HandThumb4',
			'index-finger-metacarpal': null,
			'index-finger-phalanx-proximal': 'HandIndex1',
			'index-finger-phalanx-intermediate': 'HandIndex2',
			'index-finger-phalanx-distal': 'HandIndex3',
			'index-finger-tip': 'HandIndex4',
			'middle-finger-metacarpal': null,
			'middle-finger-phalanx-proximal': 'HandMiddle1',
			'middle-finger-phalanx-intermediate': 'HandMiddle2',
			'middle-finger-phalanx-distal': 'HandMiddle3',
			'middle-finger-tip': 'HandMiddle4',
			'ring-finger-metacarpal': null,
			'ring-finger-phalanx-proximal': 'HandRing1',
			'ring-finger-phalanx-intermediate': 'HandRing2',
			'ring-finger-phalanx-distal': 'HandRing3',
			'ring-finger-tip': 'HandRing4',
			'pinky-finger-metacarpal': null,
			'pinky-finger-phalanx-proximal': 'HandPinky1',
			'pinky-finger-phalanx-intermediate': 'HandPinky2',
			'pinky-finger-phalanx-distal': 'HandPinky3',
			'pinky-finger-tip': 'HandPinky4'
		};
	},

	tick(this : VrBodySystem, d:number, dt:number)
	{
		const xr = this.el.renderer.xr;
		if( this.instance && xr && xr.enabled && xr.isPresenting )
			this.instance.updateFrame(xr.getFrame(), xr.getSession()! );
	}
})